import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Card, Modal, message, Empty, Spin } from 'antd';
import { PrinterOutlined, DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

interface PrintItem {
  id: number;
  campaign_id: number;
  campaign_name: string;
  campaign_type: string;
  first_name: string;
  last_name: string;
  email: string;
  layout_type: string;
  status: string;
  created_at: string;
}

export const PrintQueue: React.FC = () => {
  const [items, setItems] = useState<PrintItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Load print queue
  const loadPrintQueue = async () => {
    setLoading(true);
    try {
      const response = await api.get('/marketing/print-queue');
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading print queue:', error);
      message.error('Failed to load print queue');
    } finally {
      setLoading(false);
    }
  };

  // Download the generated mail piece. The backend currently renders mail pieces
  // as .html documents and serves them via res.download(), which sets the real
  // filename + extension in the Content-Disposition header. Honour that header so
  // the saved file always matches what the server produced (instead of forcing a
  // hardcoded extension), falling back to the correct .html extension if absent.
  const handleDownloadPDF = async (sendId: number) => {
    try {
      const response = await api.get(`/marketing/print/${sendId}/pdf`, {
        responseType: 'blob',
      });

      const disposition: string = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const serverFilename = match ? decodeURIComponent(match[1]) : '';

      const ext = response.data?.type === 'application/pdf' ? 'pdf' : 'html';
      const filename = serverFilename || `mail-piece-${sendId}.${ext}`;

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentElement?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading mail piece:', error);
      message.error('Failed to download mail piece');
    }
  };

  // Generate PDFs for every campaign that still has pending items in the queue.
  // The backend POST /print-batch generates per campaign, so we fire one request
  // per distinct campaign_id that has at least one pending send.
  const handleGeneratePDFs = async () => {
    const campaignIds = Array.from(
      new Set(
        items.filter((item) => item.status === 'pending').map((item) => item.campaign_id)
      )
    );

    if (campaignIds.length === 0) {
      message.info('No pending items to generate');
      return;
    }

    setGenerating(true);
    try {
      const results = await Promise.all(
        campaignIds.map((campaignId) =>
          api.post('/marketing/print-batch', { campaignId })
        )
      );

      const generated = results.reduce((sum, res) => sum + (res.data?.generated || 0), 0);
      const failed = results.reduce((sum, res) => sum + (res.data?.failed || 0), 0);

      if (failed > 0) {
        message.warning(`Generated ${generated} mail piece(s), ${failed} failed`);
      } else {
        message.success(`Generated ${generated} mail piece(s)`);
      }
      loadPrintQueue();
    } catch (error) {
      console.error('Error generating PDFs:', error);
      message.error('Failed to generate PDFs');
    } finally {
      setGenerating(false);
    }
  };

  // Mark as printed
  const handleMarkAsPrinted = async (sendId: number) => {
    try {
      await api.patch(`/marketing/sends/${sendId}`, { status: 'printed' });
      message.success('Marked as printed');
      loadPrintQueue();
    } catch (error) {
      console.error('Error updating send status:', error);
      message.error('Failed to mark as printed');
    }
  };

  // Mark selected as printed
  const handleMarkSelectedAsPrinted = async () => {
    setLoading(true);
    try {
      await Promise.all(
        selectedRowKeys.map((key) =>
          api.patch(`/marketing/sends/${key}`, { status: 'printed' })
        )
      );
      message.success('Marked selected items as printed');
      setSelectedRowKeys([]);
      loadPrintQueue();
    } catch (error) {
      console.error('Error updating send status:', error);
      message.error('Failed to mark items as printed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrintQueue();
    const interval = setInterval(loadPrintQueue, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      width: 150,
      render: (_: unknown, record: PrintItem) => (
        <span>
          {record.first_name} {record.last_name}
        </span>
      ),
    },
    {
      title: 'Campaign',
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      width: 180,
    },
    {
      title: 'Type',
      dataIndex: 'campaign_type',
      key: 'campaign_type',
      width: 120,
      render: (type: string) => (
        <Tag color={getCampaignTypeColor(type)}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Layout',
      dataIndex: 'layout_type',
      key: 'layout_type',
      width: 100,
      render: (layout: string) => (
        <Tag>{layout.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: PrintItem) => (
        <Space size="small">
          {record.status === 'generated' && (
            <>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadPDF(record.id)}
              >
                Download
              </Button>
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkAsPrinted(record.id)}
              >
                Printed
              </Button>
            </>
          )}
          {record.status === 'pending' && (
            <Tag color="orange">GENERATING</Tag>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // Group items by campaign for batch generation
  const campaigns = Array.from(
    new Map(items.map((item) => [item.campaign_name, item])).values()
  );

  return (
    <Spin spinning={loading}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2>Print Queue</h2>
          {items.length > 0 && (
            <p style={{ color: '#6b7280' }}>
              Total items: <strong>{items.length}</strong>
            </p>
          )}
        </div>

        {items.length > 0 ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  loading={generating}
                  onClick={handleGeneratePDFs}
                >
                  Generate PDFs
                </Button>
                {selectedRowKeys.length > 0 && (
                  <Button onClick={handleMarkSelectedAsPrinted} type="primary">
                    Mark {selectedRowKeys.length} as Printed
                  </Button>
                )}
                <Button onClick={loadPrintQueue}>Refresh</Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={items}
              rowKey="id"
              rowSelection={rowSelection}
              pagination={{ pageSize: 20 }}
              loading={loading}
              size="small"
            />
          </>
        ) : (
          <Empty description="No items in print queue" />
        )}
      </Card>
    </Spin>
  );
};

function getCampaignTypeColor(type: string): string {
  const colors: Record<string, string> = {
    birthday: 'blue',
    christmas: 'red',
    thank_you: 'green',
    maintenance_reminder: 'orange',
    custom_blast: 'purple',
    reengagement: 'magenta',
  };
  return colors[type] || 'default';
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'generated':
      return 'blue';
    case 'printed':
      return 'green';
    case 'pending':
      return 'orange';
    default:
      return 'default';
  }
}

export default PrintQueue;
